import { useEffect, useState } from 'react';
import { useRevalidator } from 'react-router';

import { Button } from '@ds/core';
import { Alert, Modal } from '@ds/feedback';
import { FormField, Input, Select } from '@ds/forms';
import { moeda } from '@/comuns/formato';
import { useAviso } from '@/comuns/toast';
import { dados } from '@/data/porta';
import type { ClinicaNaPlataforma, DescontoDaClinica, Plano, VagasDaClinicaNaPlataforma } from '@/data/tipos';
import { CampoDeMotivo, motivoValido } from './apoioDoPainel';
import { SITUACAO_DA_CLINICA } from './Visao';

function mensagem(e: unknown): string {
    return e instanceof Error ? e.message : 'Tente de novo.';
}

/**
 * Suspender — ou reativar — o acesso de uma clínica.
 *
 * A suspensão vale no banco: enquanto durar, ninguém da equipe lê nem escreve.
 * Reativar devolve a clínica à situação de antes, e não a "ativa" por padrão —
 * reativar uma inadimplente como ativa apagaria a dívida da tela.
 */
export function SuspensaoModal({ clinica, aberto, onFechar }: {
    clinica: ClinicaNaPlataforma;
    aberto: boolean;
    onFechar: () => void;
}) {
    const { revalidate } = useRevalidator();
    const aviso = useAviso();
    const reativar = clinica.situacao === 'suspensa';
    const [motivo, setMotivo] = useState('');
    const [salvando, setSalvando] = useState(false);
    const [erro, setErro] = useState<string | null>(null);

    useEffect(() => {
        if (aberto) {
            setMotivo('');
            setErro(null);
        }
    }, [aberto]);

    const confirmar = async () => {
        setSalvando(true);
        setErro(null);
        try {
            if (reativar) {
                const para = await dados().reativarClinica(clinica.id, motivo);
                aviso({
                    title: 'Clínica reativada',
                    description: `${clinica.nome} voltou a ${SITUACAO_DA_CLINICA[para].rotulo.toLowerCase()} · registrado na auditoria`,
                });
            } else {
                await dados().suspenderClinica(clinica.id, motivo);
                aviso({ title: 'Acesso suspenso', description: `${clinica.nome} · registrado na auditoria`, tone: 'warning' });
            }
            onFechar();
            void revalidate();
        } catch (e) {
            setErro(mensagem(e));
        } finally {
            setSalvando(false);
        }
    };

    return (
        <Modal
            open={aberto} onClose={onFechar} width={500} tone={reativar ? 'default' : 'danger'}
            title={reativar ? `Reativar ${clinica.nome}?` : `Suspender o acesso de ${clinica.nome}?`}
            description={reativar
                ? 'A equipe volta a entrar, e a clínica volta à situação em que estava antes da suspensão.'
                : 'Todos os membros perdem o acesso na hora. Os dados ficam onde estão, e reativar devolve o acesso.'}
            dirty={motivo.trim().length > 0}
            footer={(
                <>
                    <Button variant="secondary" onClick={onFechar}>Cancelar</Button>
                    <Button
                        variant={reativar ? 'primary' : 'danger'} loading={salvando} disabled={!motivoValido(motivo)}
                        onClick={() => { void confirmar(); }}
                    >
                        {reativar ? 'Reativar' : 'Suspender acesso'}
                    </Button>
                </>
            )}
        >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {erro && <Alert tone="danger" dismissible={false}>{erro}</Alert>}
                {!reativar && (
                    <Alert tone="warning" dismissible={false} title="A suspensão vale no banco">
                        Enquanto durar, ninguém da equipe da clínica lê nem escreve nada. O suporte continua podendo
                        entrar por impersonação, somente leitura.
                    </Alert>
                )}
                <CampoDeMotivo id="suspensao-motivo" valor={motivo} onChange={setMotivo} />
            </div>
        </Modal>
    );
}

/**
 * Trocar o plano da clínica.
 *
 * Muda a clínica e a assinatura juntas. Sem gateway não há cobrança
 * proporcional: a próxima fatura gerada usa o preço do plano novo, e é só isso.
 */
export function AlterarPlanoModal({ clinica, planos, aberto, onFechar }: {
    clinica: ClinicaNaPlataforma;
    planos: Plano[];
    aberto: boolean;
    onFechar: () => void;
}) {
    const { revalidate } = useRevalidator();
    const aviso = useAviso();
    const [codigo, setCodigo] = useState(clinica.plano);
    const [motivo, setMotivo] = useState('');
    const [salvando, setSalvando] = useState(false);
    const [erro, setErro] = useState<string | null>(null);

    useEffect(() => {
        if (aberto) {
            setCodigo(clinica.plano);
            setMotivo('');
            setErro(null);
        }
    }, [aberto, clinica.plano]);

    const escolhido = planos.find((p) => p.codigo === codigo);

    const confirmar = async () => {
        setSalvando(true);
        setErro(null);
        try {
            await dados().trocarPlanoDaClinica(clinica.id, codigo, motivo);
            aviso({ title: 'Plano alterado', description: `${clinica.nome} → ${escolhido?.nome ?? codigo} · registrado na auditoria` });
            onFechar();
            void revalidate();
        } catch (e) {
            setErro(mensagem(e));
        } finally {
            setSalvando(false);
        }
    };

    return (
        <Modal
            open={aberto} onClose={onFechar} width={540} title="Alterar plano"
            description={`${clinica.nome} está no plano ${clinica.plano}.`}
            dirty={codigo !== clinica.plano || motivo.trim().length > 0}
            footer={(
                <>
                    <Button variant="secondary" onClick={onFechar}>Cancelar</Button>
                    <Button
                        loading={salvando} disabled={codigo === clinica.plano || !motivoValido(motivo)}
                        onClick={() => { void confirmar(); }}
                    >
                        Confirmar alteração
                    </Button>
                </>
            )}
        >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {erro && <Alert tone="danger" dismissible={false}>{erro}</Alert>}
                <FormField label="Plano novo" htmlFor="plano-novo" required>
                    <Select
                        id="plano-novo" value={codigo} onChange={setCodigo}
                        options={planos.map((p) => ({
                            value: p.codigo,
                            label: `${p.nome} · ${p.precoCentavos === undefined ? 'preço a definir' : `${moeda(p.precoCentavos)}/${p.ciclo === 'anual' ? 'ano' : 'mês'}`}`,
                        }))}
                    />
                </FormField>
                <Alert tone="neutral" dismissible={false} title="Nada é cobrado agora">
                    Não há gateway de pagamento ligado, então não existe ajuste proporcional: a próxima fatura
                    gerada já usa o preço do plano novo.
                </Alert>
                <CampoDeMotivo id="plano-motivo" valor={motivo} onChange={setMotivo} />
            </div>
        </Modal>
    );
}

/**
 * As vagas de acesso liberadas para esta clínica (0165).
 *
 * O número do plano vale por padrão; aqui se escreve a folga — a vaga extra
 * vendida, a cortesia — sem precisar criar um plano só para uma clínica.
 * Vazio devolve o do plano, em vez de fixar um número que depois ninguém sabe
 * de onde veio.
 *
 * Rebaixar abaixo do que já está em uso é permitido e não tira acesso de
 * ninguém: a clínica apenas para de crescer. Recusar aqui impediria a
 * plataforma de corrigir uma venda.
 */
export function VagasDaClinicaModal({ clinica, vagas, aberto, onFechar }: {
    clinica: ClinicaNaPlataforma;
    vagas: VagasDaClinicaNaPlataforma;
    aberto: boolean;
    onFechar: () => void;
}) {
    const { revalidate } = useRevalidator();
    const aviso = useAviso();
    const [valor, setValor] = useState('');
    const [salvando, setSalvando] = useState(false);
    const [erro, setErro] = useState<string | null>(null);

    useEffect(() => {
        if (aberto) {
            setValor(vagas.folgaDoCadastro === null ? '' : String(vagas.folgaDoCadastro));
            setErro(null);
        }
    }, [aberto, vagas.folgaDoCadastro]);

    const original = vagas.folgaDoCadastro === null ? '' : String(vagas.folgaDoCadastro);
    const limpo = valor.trim();
    const numero = limpo === '' ? null : Number(limpo);
    const invalido = numero !== null && (!Number.isInteger(numero) || numero <= 0);

    const confirmar = async () => {
        setSalvando(true);
        setErro(null);
        try {
            const ocupadas = await dados().definirLimiteDeMembros(clinica.id, numero);
            aviso({
                title: numero === null ? 'Vagas voltaram ao plano' : 'Vagas definidas',
                description: numero === null
                    ? `${clinica.nome} volta ao limite do plano${vagas.limiteDoPlano === null ? '' : ` (${vagas.limiteDoPlano})`}.`
                    : `${clinica.nome}: ${numero} vaga(s), ${ocupadas} em uso.`,
            });
            onFechar();
            void revalidate();
        } catch (e) {
            setErro(mensagem(e));
        } finally {
            setSalvando(false);
        }
    };

    return (
        <Modal
            open={aberto} onClose={onFechar} width={520} title="Vagas de acesso"
            description={`${clinica.nome} usa ${vagas.ocupadas} de ${vagas.limite ?? 'sem limite'}.`}
            dirty={limpo !== original}
            footer={(
                <>
                    <Button variant="secondary" onClick={onFechar}>Cancelar</Button>
                    <Button loading={salvando} disabled={limpo === original || invalido} onClick={() => { void confirmar(); }}>
                        Salvar vagas
                    </Button>
                </>
            )}
        >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {erro && <Alert tone="danger" dismissible={false}>{erro}</Alert>}
                <FormField
                    label="Vagas liberadas" htmlFor="clinica-vagas"
                    hint={vagas.limiteDoPlano === null
                        ? 'Vazio = o plano desta clínica não tem limite escrito, então não há teto.'
                        : `Vazio = vale o plano (${vagas.limiteDoPlano}). Conta quem está ativo e quem tem convite pendente.`}
                >
                    <Input
                        id="clinica-vagas" type="number" min={1} value={valor}
                        onChange={setValor} placeholder={vagas.limiteDoPlano === null ? 'sem limite' : String(vagas.limiteDoPlano)}
                        invalid={invalido}
                    />
                </FormField>
                {numero !== null && numero < vagas.ocupadas && (
                    <Alert tone="warning" dismissible={false} title="Abaixo do que já está em uso">
                        {`A clínica tem ${vagas.ocupadas} pessoas com acesso. Ninguém perde o acesso: ela apenas não cadastra mais ninguém até desativar alguém.`}
                    </Alert>
                )}
            </div>
        </Modal>
    );
}

const MESES = [1, 2, 3, 6, 12, 24];

/**
 * Aplicar — ou remover — o desconto da clínica.
 *
 * Percentual sobre o preço do plano, a partir da competência de hoje. É
 * percentual justamente para não depender de um preço que ainda não foi
 * decidido: sem preço, o desconto fica registrado e não muda fatura nenhuma.
 */
export function DescontoModal({ clinica, vigente, modo, onFechar }: {
    clinica: ClinicaNaPlataforma;
    vigente: DescontoDaClinica | undefined;
    modo: 'aplicar' | 'remover' | null;
    onFechar: () => void;
}) {
    const { revalidate } = useRevalidator();
    const aviso = useAviso();
    const [percentual, setPercentual] = useState('');
    const [meses, setMeses] = useState('3');
    const [motivo, setMotivo] = useState('');
    const [salvando, setSalvando] = useState(false);
    const [erro, setErro] = useState<string | null>(null);

    useEffect(() => {
        if (modo) {
            setPercentual('');
            setMeses('3');
            setMotivo('');
            setErro(null);
        }
    }, [modo]);

    const numero = Number(percentual);
    const percentualValido = Number.isInteger(numero) && numero >= 1 && numero <= 100;

    const confirmar = async () => {
        setSalvando(true);
        setErro(null);
        try {
            if (modo === 'remover') {
                await dados().removerDesconto(clinica.id, motivo);
                aviso({ title: 'Desconto removido', description: `${clinica.nome} · registrado na auditoria` });
            } else {
                await dados().aplicarDesconto(clinica.id, { percentual: numero, meses: Number(meses), motivo });
                aviso({ title: 'Desconto aplicado', description: `${numero}% por ${meses} ${meses === '1' ? 'mês' : 'meses'} · ${clinica.nome}` });
            }
            onFechar();
            void revalidate();
        } catch (e) {
            setErro(mensagem(e));
        } finally {
            setSalvando(false);
        }
    };

    const remover = modo === 'remover';

    return (
        <Modal
            open={modo !== null} onClose={onFechar} width={500} tone={remover ? 'danger' : 'default'}
            title={remover ? 'Remover desconto?' : 'Aplicar desconto'}
            description={remover && vigente
                ? `${vigente.percentual}% de ${vigente.inicioCompetencia} a ${vigente.fimCompetencia}. As faturas geradas depois voltam ao preço cheio.`
                : `Sobre o preço do plano de ${clinica.nome}, a partir da competência de hoje.`}
            dirty={percentual.trim().length > 0 || motivo.trim().length > 0}
            footer={(
                <>
                    <Button variant="secondary" onClick={onFechar}>Cancelar</Button>
                    <Button
                        variant={remover ? 'danger' : 'primary'} loading={salvando}
                        disabled={!motivoValido(motivo) || (!remover && !percentualValido)}
                        onClick={() => { void confirmar(); }}
                    >
                        {remover ? 'Remover desconto' : 'Aplicar desconto'}
                    </Button>
                </>
            )}
        >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {erro && <Alert tone="danger" dismissible={false}>{erro}</Alert>}
                {!remover && (
                    <>
                        {vigente && (
                            <Alert tone="warning" dismissible={false} title={`Substitui o desconto de ${vigente.percentual}%`}>
                                Um desconto vivo por clínica: dois somados seriam uma conta que ninguém fez de propósito.
                            </Alert>
                        )}
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(180px,1fr))', gap: 14 }}>
                            <FormField label="Percentual" htmlFor="desconto-percentual" required hint="De 1 a 100.">
                                <Input id="desconto-percentual" type="number" value={percentual} onChange={setPercentual} placeholder="10" />
                            </FormField>
                            <FormField label="Por quantos meses" htmlFor="desconto-meses" required>
                                <Select
                                    id="desconto-meses" value={meses} onChange={setMeses}
                                    options={MESES.map((m) => ({ value: String(m), label: m === 1 ? '1 mês' : `${m} meses` }))}
                                />
                            </FormField>
                        </div>
                        <Alert tone="neutral" dismissible={false} title="Fatura já gerada não muda">
                            O desconto entra nas faturas geradas daqui em diante. Sem preço no plano, ele fica
                            registrado e não muda nada — porque ainda não há fatura.
                        </Alert>
                    </>
                )}
                <CampoDeMotivo id="desconto-motivo" valor={motivo} onChange={setMotivo} />
            </div>
        </Modal>
    );
}
